import { useNavigate } from 'react-router-dom';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import { useEffect, useState } from 'react';
import { getApplications } from '../services/ApplicationService';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Badge from '../components/badge/Badge';
import Table from '../components/table/table';
import { formatCurrency } from '../utils/Masks';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { format } from 'date-fns';
import { APPLICATION_STATUS } from '../utils/Constants';
import {
  getStatusText,
  isReadyToInstrumentStatus,
} from '../utils/applicationStatusUtils';
import Input from '../components/input/Input';
import Spinner from '../components/Spinner/spinner';
import Tooltip from '../components/tooltip/Tooltip';
import Span from '../components/texts/span/Span';
import TruncatedTextWithTooltip from '../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import { hasData, normalizeCuitForSearch } from '../utils/Utils';
import { routes } from '../routes/routePaths';
import { IItem, Nullable } from '../types/interfaces';
import { Toast } from '../components/toast/toast';
import CommitteeButton from '../components/committeeButton/CommitteeButton';
import Icon from '../components/icon/Icon';
import { useOfficers } from '../hooks/useOfficers';
import FilterModal from '../components/modals/FilterModal';
import { getFilters } from '../services/ApplicationService';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import { useConfig } from '../context/ConfigContext';
import { commercialCellFromOfficerPath } from '../utils/dashboardListCommercialCell';
import { useRestoreListState } from '../hooks/useRestoreListState';
import { buildListState, DASHBOARD_KEYS } from '../utils/listNavigationState';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';

interface ITableData {
  size: number;
  page: number;
  totalElements: number;
  totalPages: number;
}

// Constants
const ENTER_KEY_CODE = 13;

interface IRow {
  cuit: string;
  id: number;
  commercial: string;
  companyName: string;
  producerName: string;
  producerLastName: string;
  updateDate: string;
  status: number;
  statusCode?: string;
  statusText?: string;
  totalArs: number;
  totalUsd: number;
  approved_total_ars: Nullable<number>;
  approved_total_usd: Nullable<number>;
  origen?: string;
  referido?: string;
}

const InstrumentationDashboard = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const { officers } = useOfficers();
  const appConfig = useConfig();
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(appConfig);
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const commercialColumnTitle = isManagementSelectionEnabled ? 'Gerencia' : 'Comercial';
  const restoredList = useRestoreListState(DASHBOARD_KEYS.instrumentation);
  const [applications, setApplications] = useState<IRow[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(restoredList.page);
  const [searchText, setSearchText] = useState<string>(restoredList.searchText);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedApplications, setSelectedApplications] = useState<Set<number>>(
    restoredList.selectedApplications,
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    restoredList.activeFilters,
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [isClearFiltersHovered, setIsClearFiltersHovered] =
    useState<boolean>(false);

  const mapApplications = (data: any[]): IRow[] => {
    return data
      .filter(
        (item) =>
          item.status.code === APPLICATION_STATUS.instrumentation.label ||
          item.status.code === APPLICATION_STATUS.readyToInstrument.label
      )
      .map((item) => ({
        cuit: item.cuit,
        id: item.id,
        commercial: commercialCellFromOfficerPath(
          item as IItem,
          officers,
          isManagementSelectionEnabled,
        ),
        companyName: item.company_name,
        producerName: item.producer.first_name,
        producerLastName: item.producer.last_name,
        updateDate: item.updated_date,
        status: item.status.id,
        statusCode: item.status.code,
        statusText: item.status.name || item.status.text,
        totalArs: item.total_ars,
        totalUsd: item.total_usd,
        approved_total_ars: item.approved_total_ars,
        approved_total_usd: item.approved_total_usd,
        origen: item.origen || 'Comercial',
        referido: item.referido || 'Generación propia',
      }));
  };

  const fetchApplications = (filters?: Record<string, string>) => {
    const filtersToApply = filters || activeFilters;

    let statusesToSearch = [
      APPLICATION_STATUS.instrumentation.label,
      APPLICATION_STATUS.readyToInstrument.label,
    ];

    if (filtersToApply.status) {
      statusesToSearch = [filtersToApply.status];
    }

    getApplications(
      page,
      statusesToSearch,
      normalizeCuitForSearch(searchText),
      filtersToApply
    ).then((response) => {
      if (hasData(response) && response.data?.content) {
        setTableData({
          size: response.data.size,
          page: response.data.page,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages + 1,
        });

        setApplications(mapApplications(response.data.content));
      }
    });
  };

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('application');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    fetchApplications(activeFilters);
  }, [page, officers, isManagementSelectionEnabled, JSON.stringify(activeFilters)]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleRowClicked = (data: any) => {
    const listState = buildListState({
      dashboardKey: DASHBOARD_KEYS.instrumentation,
      page,
      searchText,
      activeFilters,
      selectedApplications,
    });

    navigate(
      routes.instrumentation +
        '/' +
        data.cuit +
        '/applications/' +
        data.id +
        '/steps',
      {
        state: { status: data.status, statusCode: data.statusCode, listState },
      }
    );
  };

  const handleSelectApplication = (applicationId: number) => {
    const newSelected = new Set(selectedApplications);
    if (newSelected.has(applicationId)) {
      newSelected.delete(applicationId);
    } else {
      newSelected.add(applicationId);
    }
    setSelectedApplications(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedApplications.size === applications.length) {
      setSelectedApplications(new Set());
    } else {
      setSelectedApplications(new Set(applications.map((app) => app.id)));
    }
  };

  const handleViewSolicitud = (row: IRow) => {
    const listState = buildListState({
      dashboardKey: DASHBOARD_KEYS.instrumentation,
      page,
      searchText,
      activeFilters,
      selectedApplications,
    });

    navigate(
      routes.instrumentation + '/' + row.cuit + '/applications/' + row.id,
      {
        state: {
          status: row.status,
          statusCode: row.statusCode,
          viewOnly: true,
          listState,
        },
      }
    );
  };

  const columns = [
    {
      name: (
        <Card display='flex' alignItems='center' gap='8px'>
          <input
            type='checkbox'
            checked={
              selectedApplications.size === applications.length &&
              applications.length > 0
            }
            onChange={handleSelectAll}
            style={{ cursor: 'pointer' }}
          />
          <P type='3' variant='neutral' weight='bold'>
            {fiscalId.label}
          </P>
        </Card>
      ),
      selector: (row: IRow) => (
        <Card
          display='flex'
          alignItems='center'
          gap='8px'
          onClick={() => handleRowClicked(row)}
        >
          <input
            type='checkbox'
            checked={selectedApplications.has(row.id)}
            onChange={() => handleSelectApplication(row.id)}
            style={{ cursor: 'pointer' }}
            onClick={(e) => e.stopPropagation()}
          />
          <TruncatedTextWithTooltip text={fiscalId.format(row.cuit)}>
            <P type='3' variant='primary'>
              {fiscalId.format(row.cuit)}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '90px',
      grow: 0.19,
    },
    {
      name: 'Origen',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row.origen || 'Comercial'}>
            <P type='3' variant='neutral'>
              {row.origen || 'Comercial'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      
      grow: 0.1,
    },
    {
      name: 'Usuario',
      selector: (row: IRow) => (
        <div style={{ width: '100%' }} onClick={() => handleRowClicked(row)}>
          <Card>
            <TruncatedTextWithTooltip text={row.producerName + ' ' + row.producerLastName}>
              <P type='3' variant='neutral' withMargin={false}>
                {row.producerName + ' ' + row.producerLastName}
              </P>
            </TruncatedTextWithTooltip>
          </Card>
        </div>
      ),
      
      grow: 0.2,
    },
    {
      name: 'Referido/Vendedor',
      selector: (row: IRow) => (
        <div style={{ width: '100%' }} onClick={() => handleRowClicked(row)}>
          <Card>
            <TruncatedTextWithTooltip text={row.referido || 'Generación propia'}>
              <Span type='3' variant='neutral'>
                {row.referido || 'Generación propia'}
              </Span>
            </TruncatedTextWithTooltip>
          </Card>
        </div>
      ),
      minWidth: '80px',
      grow: 0.19,
    },
    {
      name: commercialColumnTitle,
      selector: (row: IRow) => (
        <div style={{ width: '100%' }} onClick={() => handleRowClicked(row)}>
          <Card>
            <TruncatedTextWithTooltip text={row.commercial}>
              <Span type='3' variant='neutral'>
                {row.commercial}
              </Span>
            </TruncatedTextWithTooltip>
          </Card>
        </div>
      ),
    
      grow: 0.2,
    },
    {
      name: 'Razón social',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row.companyName}>
            <Span type='3' variant='neutral'>
              {row.companyName}
            </Span>
          </TruncatedTextWithTooltip>
        </Card>
      ),
     
      grow: 0.12,
    },
    {
      name: 'Modificado',
      cell: (row: IRow) => {
        const date = new Date(row.updateDate);
        return (
          <Card onClick={() => handleRowClicked(row)}>
            <P type='3' variant='neutral'>
              {row.updateDate ? format(date, 'dd/MM/yyyy') : ''}
            </P>
          </Card>
        );
      },
      minWidth: '90px',
      grow: 0.1,
    },
    {
      name: 'Monto',
      cell: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <P type='3' variant='neutral'>
            {localCurrencyCode}: {formatCurrency(row.approved_total_ars ?? 0, true)}
          </P>
          <P type='3' variant='neutral'>
            USD: {formatCurrency(row.approved_total_usd ?? 0, true)}
          </P>
        </Card>
      ),
      minWidth: '160px',
      grow: 0.1,
    },
    {
      name: (
        <Card display='flex' justifyContent='center' width='100%'>
          <P type='3' variant='neutral'>
            Solicitud
          </P>
        </Card>
      ),
      cell: (row: IRow) => (
        <Card
          display='flex'
          justifyContent='center'
          alignItems='center'
          width='100%'
        >
          <Icon
            name='view_outline'
            variant='info'
            height={24}
            width={24}
            cursor='pointer'
            onClick={() => handleViewSolicitud(row)}
          />
        </Card>
      ),
      minWidth: '60px',
      grow: 0.1,
    },
    {
      name: 'Estado',
      id: 'status',
      cell: (row: IRow) => {
        const statusKey = row.statusCode ?? row.status;
        const statusText = getStatusText(statusKey, row.statusText);
        const statusVariant = isReadyToInstrumentStatus(statusKey)
          ? 'primary'
          : 'success';

        return (
          <Card alignItems='center' onClick={() => handleRowClicked(row)}>
            <Tooltip position='top' msg={statusText}>
              <Badge variant={statusVariant} type='text'>
                {statusText}
              </Badge>
            </Tooltip>
          </Card>
        );
      },
      minWidth: '150px',
      grow: 0,
    },
  ];

  const handleSearch = (keycode: number) => {
    if (keycode === ENTER_KEY_CODE) {
      setIsSearching(true);
      
      // Determinar qué estados buscar
      let statusesToSearch = [
        APPLICATION_STATUS.instrumentation.label,
        APPLICATION_STATUS.readyToInstrument.label,
      ];
      
      // Si hay un filtro de estado aplicado, usar solo ese
      if (activeFilters.status) {
        statusesToSearch = [activeFilters.status];
      }
      
      getApplications(
        page,
        statusesToSearch,
        normalizeCuitForSearch(searchText),
        activeFilters
      ).then((response) => {
        if (hasData(response) && response.status === 200) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          setApplications(mapApplications(response.data.content));
        }

        setIsSearching(false);
      });
    }
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setActiveFilters(queryParams);
    setPage(0); // Resetear a la primera página cuando se aplican filtros
    fetchApplications(queryParams);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
    fetchApplications({});
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    if (activeFilters.commercialId) {
      const commercial = filterData.commercials.find(
        (c) => c.id.toString() === activeFilters.commercialId
      );
      if (commercial) {
        labels.push({
          type: 'Comercial',
          label: commercial.name,
          filterKey: 'commercialId',
        });
      } else {
        labels.push({
          type: 'Comercial',
          label: 'Desconocido',
          filterKey: 'commercialId',
        });
      }
    }

    if (activeFilters.managementId) {
      const idLookup =
        activeFilters.managementId === 'null'
          ? '-1'
          : activeFilters.managementId;
      const mgmt = filterData.managements?.find((m) => m.id === idLookup);
      if (mgmt) {
        labels.push({
          type: 'Gerencia',
          label: mgmt.name,
          filterKey: 'managementId',
        });
      } else {
        labels.push({
          type: 'Gerencia',
          label: 'Desconocido',
          filterKey: 'managementId',
        });
      }
    }

    if (activeFilters.producerId) {
      const producer = filterData.producers.find(
        (p) => p.id.toString() === activeFilters.producerId
      );
      if (producer) {
        labels.push({
          type: 'Usuario',
          label: producer.name,
          filterKey: 'producerId',
        });
      } else {
        labels.push({
          type: 'Usuario',
          label: 'Desconocido',
          filterKey: 'producerId',
        });
      }
    }

    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido/Vendedor',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido/Vendedor',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    if (activeFilters.status) {
      const status = filterData.status.find(
        (s) => s.id.toString() === activeFilters.status
      );
      if (status) {
        const statusObj = Object.values(APPLICATION_STATUS).find(
          (s) => s.code === status.id
        );
        const statusLabel = statusObj ? statusObj.text : status.name;
        labels.push({
          type: 'Estado',
          label: statusLabel,
          filterKey: 'status',
        });
      } else {
        labels.push({
          type: 'Estado',
          label: 'Desconocido',
          filterKey: 'status',
        });
      }
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(0);
    fetchApplications(newFilters);
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Toast id='committee-toast' />
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
        </Card>
      </Card>
      <Card display='flex' justifyContent='space-between' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Card display='flex' gap='16px' alignItems='center'>
          <Button variant='secondary-1' onClick={handleOpenModal}>
            <Card display='flex' alignItems='center' gap='8px'>
              <Icon name='other_filter' variant='secondary-1' width={20} height={20} />
              <P variant='secondary-1' withMargin={false}>Editar filtros</P>
            </Card>
          </Button>
          <Card display='flex' gap='16px' alignItems='center'>
            <Card width='280px'>
              <Input
                type='text'
                size='md'
                icons={{ iconLeft: 'search_outline' }}
                placeholder='Buscar cliente'
                onChange={(e) => setSearchText(e.target.value)}
                onKeyup={(e) => handleSearch(e.keyCode)}
              />
            </Card>
            <Button
              variant='tertiary'
              size='sm'
              onClick={() => handleSearch(ENTER_KEY_CODE)}
            >
              {isSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
            </Button>
          </Card>
        </Card>
        <CommitteeButton
            selectedApplications={selectedApplications}
            applications={applications}
            onSuccess={() => {
              setSelectedApplications(new Set());
              fetchApplications(activeFilters);
            }}
            variant='custom'
            size='lg'
            disabled={false}
          />
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P variant='neutral' withMargin={false} type='3' weight='bold'>
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{ color: '#FFFFFF', fontSize: '11px', lineHeight: '16px', letterSpacing: '0.3px' }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P variant='neutral' withMargin={false} type='3' style={{ color: activeFilters.status ? '#A16108' : '#585053' }}>
              Borrar filtros
            </P>
          </div>
        </div>
      )}
      {applications.length !== 0 ? (
        <Card margin={{ top: '5' }} width='100%'>
          <style>
            {`
            .instrumentation-table-scroll .rdt_TableHead .rdt_TableCol[data-column-id="status"],
            .instrumentation-table-scroll .rdt_TableBody .rdt_TableCell[data-column-id="status"] {
              position: sticky !important;
              right: 0 !important;
              z-index: 2 !important;
              background: #FFFFFF !important;
            }
            .instrumentation-table-scroll .rdt_TableHead .rdt_TableCol[data-column-id="status"] {
              background: #F5F7FA !important;
            }
            `}
          </style>
          <div
            className='instrumentation-table-scroll'
            style={{
              width: '100%',
              overflowX: 'auto',
            }}
          >
            <Table
              columns={columns}
              data={applications}
              tableData={tableData}
              handleChangePage={(e: any) => handleChangePage(e)}
              onRowClicked={(e: any) => handleRowClicked(e)}
              customStyles={{ table: { style: { minWidth: '100%' } } }}
            />
          </div>
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay carpetas en curso.
          </P>
          <P variant='primary' align='center'>
            Podés crear una nueva.
          </P>
          <Button variant='primary' onClick={() => navigate(routes.clients)}>
            Alta de nueva carpeta
          </Button>
        </Card>
      )}
      
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={!isManagementSelectionEnabled}
        showManagementFilter={isManagementSelectionEnabled}
        showProducerFilter={true}
        showReferrerFilter={true}
        showStatusFilter={true}
        allowedStatusIds={['INS', 'RTI']}
        initialFilters={activeFilters}
      />
    </Card>
  );
};

export default InstrumentationDashboard;